import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  SkipBack, SkipForward, ChevronDown, Loader2, BookOpen,
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { fetchChapters, fetchSlokasByChapter, getErrorMessage } from '../api/client';
import WordBreakdown from './WordBreakdown';
import { slokaVideoUrl } from '../utils/mediaUrls';
import { PlayerMobileNav, PlayerSidebar } from './listen/PlayerNav';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';

const VERSE_COUNTS = [47,72,43,42,29,47,30,28,34,42,55,20,35,27,20,24,28,78];

function fmt(s) {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
}

const Watch = () => {
  const [searchParams] = useSearchParams();
  const initChapter = parseInt(searchParams.get('chapter') || '1', 10);

  const [chapters, setChapters]       = useState([]);
  const [slokas, setSlokas]           = useState([]);
  const [chapterNum, setChapterNum]   = useState(initChapter);
  const [verseIdx, setVerseIdx]       = useState(0);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [videoError, setVideoError]   = useState(false);
  const [showChapterPicker, setShowChapterPicker] = useState(false);

  // Playback
  const [isPlaying, setIsPlaying]     = useState(false);
  const [isMuted, setIsMuted]         = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);

  // Fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showOverlay, setShowOverlay]   = useState(false);

  const videoRef       = useRef(null);
  const playerRef      = useRef(null);   // fullscreen target: video + overlay only
  const overlayTimer   = useRef(null);
  const sidebarListRef = useRef(null);

  const currentSloka = slokas[verseIdx] || null;

  // --- Data ---
  const loadChapters = useCallback(async () => {
    try {
      const d = await fetchChapters(DEFAULT_LANGUAGE);
      setChapters(d.sort((a, b) => a.chapterNumber - b.chapterNumber));
    } catch {}
  }, []);

  const loadSlokas = useCallback(async (chNum) => {
    setLoading(true); setError(null); setVideoError(false);
    try {
      const d = await fetchSlokasByChapter(DEFAULT_LANGUAGE, chNum);
      setSlokas(d.sort((a, b) => a.slokaNumber - b.slokaNumber));
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadChapters(); }, [loadChapters]);

  useEffect(() => {
    const targetVerse = parseInt(searchParams.get('verse') || '1', 10);
    loadSlokas(chapterNum).then(() => setVerseIdx(Math.max(0, targetVerse - 1)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterNum]);

  useEffect(() => {
    if (!currentSloka) return;
    setVideoError(false);
    if (videoRef.current) {
      // Drive src change imperatively so the <video> element stays mounted
      // (avoids the black-frame flash that key={src} unmount/remount causes)
      videoRef.current.src = slokaVideoUrl(chapterNum, currentSloka.slokaNumber);
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verseIdx, currentSloka]);

  // Scroll sidebar to active verse
  useEffect(() => {
    if (!sidebarListRef.current) return;
    const el = sidebarListRef.current.querySelector('.watch-sidebar-item--active');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [verseIdx]);

  // --- Fullscreen ---
  useEffect(() => {
    const onFS = () => setIsFullscreen(
      !!(document.fullscreenElement || document.webkitFullscreenElement)
    );
    const onWebkitFS = () => {
      // iOS fires webkitfullscreenchange on the video element
      const vid = videoRef.current;
      setIsFullscreen(!!(vid && vid.webkitDisplayingFullscreen));
    };
    document.addEventListener('fullscreenchange', onFS);
    document.addEventListener('webkitfullscreenchange', onFS);
    videoRef.current?.addEventListener('webkitbeginfullscreen', onWebkitFS);
    videoRef.current?.addEventListener('webkitendfullscreen', onWebkitFS);
    return () => {
      document.removeEventListener('fullscreenchange', onFS);
      document.removeEventListener('webkitfullscreenchange', onFS);
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const isFS = !!(document.fullscreenElement || document.webkitFullscreenElement);
    if (!isFS) {
      // iOS Safari: only <video> supports native fullscreen
      const vid = videoRef.current;
      if (vid && vid.webkitEnterFullscreen) {
        vid.webkitEnterFullscreen();
      } else {
        await playerRef.current?.requestFullscreen?.();
      }
      bumpOverlay();
    } else {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bumpOverlay = useCallback(() => {
    setShowOverlay(true);
    clearTimeout(overlayTimer.current);
    overlayTimer.current = setTimeout(() => setShowOverlay(false), 3000);
  }, []);

  // --- Navigation ---
  const goNext = useCallback(() => {
    if (verseIdx < slokas.length - 1) {
      setVerseIdx(v => v + 1);
    } else if (chapterNum < 18) {
      setChapterNum(c => c + 1); setVerseIdx(0);
    }
  }, [verseIdx, slokas.length, chapterNum]);

  const goPrev = useCallback(() => {
    if (verseIdx > 0) {
      setVerseIdx(v => v - 1);
    } else if (chapterNum > 1) {
      setChapterNum(c => c - 1);
      setVerseIdx(VERSE_COUNTS[chapterNum - 2] - 1);
    }
  }, [verseIdx, chapterNum]);

  const goNextChapter = useCallback(() => {
    if (chapterNum < 18) { setChapterNum(c => c + 1); setVerseIdx(0); }
  }, [chapterNum]);

  const goPrevChapter = useCallback(() => {
    if (chapterNum > 1) { setChapterNum(c => c - 1); setVerseIdx(0); }
  }, [chapterNum]);

  const selectChapter = useCallback((num) => {
    setChapterNum(num); setVerseIdx(0); setShowChapterPicker(false);
  }, []);

  // --- Video ---
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.paused
      ? videoRef.current.play().catch(() => {})
      : videoRef.current.pause();
  }, []);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  }, []);

  const handleSeek = useCallback((e) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    videoRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  }, [duration]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === ' ' || e.key === 'k') { e.preventDefault(); togglePlay(); }
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
      if (e.key === 'm' || e.key === 'M') toggleMute();
      if (e.key === 'ArrowRight' && !e.altKey && videoRef.current) videoRef.current.currentTime += 5;
      if (e.key === 'ArrowLeft'  && !e.altKey && videoRef.current) videoRef.current.currentTime -= 5;
      if (e.key === 'ArrowRight' && e.altKey) goNext();
      if (e.key === 'ArrowLeft'  && e.altKey) goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, toggleFullscreen, toggleMute, goNext, goPrev]);

  const canNext = verseIdx < slokas.length - 1 || chapterNum < 18;
  const canPrev = verseIdx > 0 || chapterNum > 1;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Preload the next verse's video so the transition is buffer-ready
  const nextSloka = slokas[verseIdx + 1] || null;
  const nextVideoSrc = nextSloka
    ? slokaVideoUrl(chapterNum, nextSloka.slokaNumber)
    : null;

  // Sidebar verse list (shared between desktop sidebar and mobile drawer)
  const verseList = (
    <div className="watch-sidebar-list" ref={sidebarListRef}>
      <p className="watch-sidebar-chapter-label">Ch {chapterNum} · {slokas.length} verses</p>
      {slokas.map((sloka, i) => (
        <button
          key={sloka._id}
          type="button"
          onClick={() => setVerseIdx(i)}
          className={`watch-sidebar-item ${i === verseIdx ? 'watch-sidebar-item--active' : ''}`}
        >
          <span className="watch-sidebar-num">{sloka.slokaNumber}</span>
          <span className="watch-sidebar-text">{sloka.sloka}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="spotify-shell spotify-shell--fit watch-page-shell">
      {/* Mobile top nav */}
      <PlayerMobileNav active="watch" showSettings={false} onToggleSettings={() => {}} />

      {/* Desktop left sidebar with nav + verse list */}
      <PlayerSidebar active="watch">
        {verseList}
      </PlayerSidebar>

      {/* Main content */}
      <div className="spotify-main-wrap spotify-main-wrap--fit watch-main-wrap">
        {/* Watch header bar */}
        <header className="watch-header">
          <div className="watch-header-left">
            <button
              type="button"
              className="watch-chapter-picker-btn"
              onClick={() => setShowChapterPicker(v => !v)}
            >
              Chapter {chapterNum}
              <ChevronDown className={`w-4 h-4 transition-transform ${showChapterPicker ? 'rotate-180' : ''}`} />
            </button>

            {currentSloka && (
              <Link
                to={`/read/${chapterNum}`}
                className="watch-read-btn"
              >
                <BookOpen className="w-4 h-4" />
                <span>Read</span>
              </Link>
            )}
          </div>

          {currentSloka && (
            <span className="watch-header-verse-badge">
              Ch {chapterNum} · V {currentSloka.slokaNumber} · {verseIdx + 1}/{slokas.length}
            </span>
          )}
        </header>

        {/* Chapter picker dropdown */}
        {showChapterPicker && (
          <div className="watch-chapter-dropdown" onClick={e => e.stopPropagation()}>
            {Array.from({ length: 18 }, (_, i) => i + 1).map(num => (
              <button
                key={num}
                type="button"
                onClick={() => selectChapter(num)}
                className={`watch-chapter-option ${num === chapterNum ? 'active' : ''}`}
              >
                <span className="watch-chapter-num">{num}</span>
                <span className="watch-chapter-name">
                  {chapters.find(c => c.chapterNumber === num)?.chapterName || `Chapter ${num}`}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="watch-body">
          {loading ? (
            <div className="watch-loading"><Loader2 className="w-8 h-8 animate-spin text-gita-gold" /></div>
          ) : error ? (
            <div className="watch-error">
              <p className="text-white/60">{error}</p>
              <button type="button" onClick={() => loadSlokas(chapterNum)} className="player-btn-primary mt-4">Retry</button>
            </div>
          ) : (
            <>
              {/* ── Video player ── */}
              <div
                ref={playerRef}
                className={`watch-player ${isFullscreen ? 'watch-player--fullscreen' : ''}`}
                onMouseMove={() => { if (isFullscreen) bumpOverlay(); }}
                onClick={() => { if (isFullscreen) { bumpOverlay(); } else { togglePlay(); } }}
              >
                {videoError ? (
                  <div className="watch-video-placeholder">
                    <p className="text-white/40 text-sm">Video not available for this verse</p>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      className="watch-video"
                      playsInline
                      preload="auto"
                      muted={isMuted}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={goNext}
                      onError={() => setVideoError(true)}
                      onTimeUpdate={() => videoRef.current && setCurrentTime(videoRef.current.currentTime)}
                      onLoadedMetadata={() => videoRef.current && setDuration(videoRef.current.duration)}
                    />
                    {/* Preload next verse so it buffers ahead — eliminates transition delay */}
                    {nextVideoSrc && (
                      <video
                        key={nextVideoSrc}
                        preload="auto"
                        aria-hidden="true"
                        style={{ display: 'none', position: 'absolute', width: 0, height: 0 }}
                      >
                        <source src={nextVideoSrc} type="video/mp4" />
                      </video>
                    )}
                  </>
                )}

                {/* Always-visible fullscreen button on video */}
                {!isFullscreen && (
                  <button
                    type="button"
                    className="watch-video-fs-btn"
                    onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                    title="Fullscreen (F)"
                  >
                    <Maximize className="w-4 h-4" />
                  </button>
                )}

                {/* Fullscreen overlay */}
                {isFullscreen && (
                  <div
                    className={`watch-fs-overlay ${showOverlay ? 'watch-fs-overlay--visible' : ''}`}
                    onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                  >
                    <div className="watch-fs-top">
                      {currentSloka && (
                        <span className="watch-fs-title">
                          Ch {currentSloka.chapterNumber} · Verse {currentSloka.slokaNumber}
                          {slokas.length > 0 && ` · ${verseIdx + 1}/${slokas.length}`}
                        </span>
                      )}
                    </div>

                    <div className="watch-fs-bottom" onClick={e => e.stopPropagation()}>
                      <div className="watch-fs-progress" onClick={handleSeek}>
                        <div className="watch-fs-progress-fill" style={{ width: `${progress}%` }} />
                        <div className="watch-fs-progress-thumb" style={{ left: `${progress}%` }} />
                      </div>
                      <div className="watch-fs-times">
                        <span>{fmt(currentTime)}</span>
                        <span>-{fmt(Math.max(0, duration - currentTime))}</span>
                      </div>

                      <div className="watch-fs-controls">
                        <div className="watch-fs-controls-left">
                          <button type="button" onClick={goPrevChapter} disabled={chapterNum <= 1} className="watch-fs-btn" title="Prev chapter">
                            <ChevronsLeft className="w-5 h-5" />
                          </button>
                          <button type="button" onClick={goPrev} disabled={!canPrev} className="watch-fs-btn" title="Prev verse">
                            <SkipBack className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                            className="watch-fs-btn watch-fs-btn--primary"
                          >
                            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                          </button>
                          <button type="button" onClick={goNext} disabled={!canNext} className="watch-fs-btn" title="Next verse">
                            <SkipForward className="w-5 h-5" />
                          </button>
                          <button type="button" onClick={goNextChapter} disabled={chapterNum >= 18} className="watch-fs-btn" title="Next chapter">
                            <ChevronsRight className="w-5 h-5" />
                          </button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); toggleMute(); }} className="watch-fs-btn">
                            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                          </button>
                        </div>
                        <div className="watch-fs-controls-right">
                          {currentSloka && (
                            <span className="watch-fs-sloka-preview">{currentSloka.sloka?.slice(0, 80)}</span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                            className="watch-fs-btn"
                            title="Exit fullscreen"
                          >
                            <Minimize className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress bar below video */}
              {!isFullscreen && (
                <div className="watch-progress-bar" onClick={handleSeek}>
                  <div className="watch-progress-fill" style={{ width: `${progress}%` }} />
                </div>
              )}

              {/* Verse info */}
              {!isFullscreen && currentSloka && (
                <div className="watch-verse-info">
                  <p className="watch-sloka-text">{currentSloka.sloka}</p>
                  <WordBreakdown
                    chapterNumber={currentSloka.chapterNumber}
                    slokaNumber={currentSloka.slokaNumber}
                    language={DEFAULT_LANGUAGE}
                    noToggle
                    className="word-breakdown--dark"
                  />
                  <p className="watch-meaning-text">{currentSloka.meaning}</p>
                </div>
              )}

              {/* Controls */}
              {!isFullscreen && (
                <div className="watch-controls">
                  <button type="button" onClick={goPrevChapter} disabled={chapterNum <= 1} className="watch-chapter-nav-btn" title="Prev chapter">
                    <ChevronsLeft className="w-4 h-4" />
                    <span className="hidden sm:inline text-xs">Ch {chapterNum - 1 > 0 ? chapterNum - 1 : ''}</span>
                  </button>

                  <button type="button" onClick={goPrev} disabled={!canPrev} className="watch-nav-btn">
                    <SkipBack className="w-5 h-5" />
                    <span className="hidden sm:inline text-sm">Prev</span>
                  </button>

                  <div className="watch-scrub-area">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                      className="watch-play-btn"
                      title={isPlaying ? 'Pause' : 'Play'}
                    >
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                    <div className="watch-scrub-strip">
                      {slokas.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setVerseIdx(i)}
                          className={`watch-scrub-dot ${i === verseIdx ? 'active' : i < verseIdx ? 'done' : ''}`}
                          aria-label={`Verse ${i + 1}`}
                        />
                      ))}
                    </div>
                  </div>

                  <button type="button" onClick={goNext} disabled={!canNext} className="watch-nav-btn">
                    <span className="hidden sm:inline text-sm">Next</span>
                    <SkipForward className="w-5 h-5" />
                  </button>

                  <button type="button" onClick={goNextChapter} disabled={chapterNum >= 18} className="watch-chapter-nav-btn" title="Next chapter">
                    <span className="hidden sm:inline text-xs">Ch {chapterNum < 18 ? chapterNum + 1 : ''}</span>
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Watch;
